import { useEffect, useState } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Button, FormText, Row, Col
} from 'reactstrap';
import Select from 'react-select';
import { IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const AddEditAuditorModal = ({
  mode,
  open,
  onClose,
  formik,
  user
}) => {
  const isAddMode = mode === 'add';
  const isEditMode = mode === 'edit';
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const companyId = user?.companyId || user?.company_id || user?.id;
    if (companyId) {
      formik.setFieldValue('company_id', companyId);
    }
  }, [user]);

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#007bff' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      '&:hover': {
        borderColor: '#007bff',
      },
      minHeight: '38px',
      fontSize: '14px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#007bff'
        : state.isFocused
          ? '#f8f9fa'
          : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#e9ecef',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#495057',
    }),
  };

  const statusOptions = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' },
  ];

  const getFieldValue = (field, defaultValue = '') => {
    return formik.values[field] ?? defaultValue;
  };

  const getNestedFieldValue = (nestedField, field, defaultValue = '') => {
    return formik.values[nestedField]?.[field] ?? defaultValue;
  };

  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    formik.setFieldValue(name, type === 'checkbox' ? checked : value);
  };

  const handleNestedFieldChange = (nestedField, field) => (e) => {
    const { value } = e.target;
    formik.setFieldValue(`${nestedField}.${field}`, value);
  };

  const handleStatusChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : true;
    formik.setFieldValue('status', value);
  };

  const getFieldError = (field) => {
    return formik.touched[field] && formik.errors[field] ? formik.errors[field] : null;
  };

  const getNestedFieldError = (nestedField, field) => {
    return formik.touched[nestedField]?.[field] && formik.errors[nestedField]?.[field]
      ? formik.errors[nestedField][field] : null;
  };

  const handleFieldBlur = (e) => {
    const { name } = e.target;
    formik.setFieldTouched(name, true);
  };

  const handleNestedFieldBlur = (nestedField, field) => () => {
    formik.setFieldTouched(`${nestedField}.${field}`, true);
  };

  const handleStatusBlur = () => {
    formik.setFieldTouched('status', true);
  };

  const getStatusValue = () => {
    const value = getFieldValue('status', true);
    return statusOptions.find(option => option.value === value) || null;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Modal isOpen={open} toggle={onClose} size="lg">
      <Form onSubmit={formik.handleSubmit}>
        <ModalHeader toggle={onClose}>
          {isEditMode ? 'Edit Auditor' : 'Add Auditor'}
        </ModalHeader>

        <ModalBody>
          <Row className="mb-3">
            <Col md={6}>
              <FormGroup>
                <Label for="email" style={{ fontSize: '0.875rem' }}>Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={getFieldValue('email')}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('email')}
                  required
                />
                {getFieldError('email') && (
                  <FormText color="danger">{getFieldError('email')}</FormText>
                )}
              </FormGroup>
            </Col>
            {isAddMode && (
              <Col md={6}>
                <FormGroup style={{ position: 'relative' }}>
                  <Label for="password" style={{ fontSize: '0.875rem' }}>Password *</Label>
                  <div style={{ position: 'relative' }}>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={getFieldValue('password')}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      disabled={formik.isSubmitting}
                      invalid={!!getFieldError('password')}
                      required
                      style={{ paddingRight: '48px' }}
                    />
                    <IconButton
                      onClick={togglePasswordVisibility}
                      disabled={formik.isSubmitting}
                      size="small"
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                        padding: '4px',
                      }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </div>
                  {getFieldError('password') && (
                    <FormText color="danger">{getFieldError('password')}</FormText>
                  )}
                </FormGroup>
              </Col>
            )}
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <FormGroup>
                <Label for="first_name" style={{ fontSize: '0.875rem' }}>First Name *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={getFieldValue('first_name')}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('first_name')}
                  required
                />
                {getFieldError('first_name') && (
                  <FormText color="danger">{getFieldError('first_name')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="last_name" style={{ fontSize: '0.875rem' }}>Last Name *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={getFieldValue('last_name')}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('last_name')}
                  required
                />
                {getFieldError('last_name') && (
                  <FormText color="danger">{getFieldError('last_name')}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={4}>
              <FormGroup>
                <Label for="mobile.country_code" style={{ fontSize: '0.875rem' }}>Country Code</Label>
                <Input
                  id="mobile.country_code"
                  name="mobile.country_code"
                  value={getNestedFieldValue('mobile', 'country_code')}
                  onChange={handleNestedFieldChange('mobile', 'country_code')}
                  onBlur={handleNestedFieldBlur('mobile', 'country_code')}
                  disabled={formik.isSubmitting}
                  invalid={!!getNestedFieldError('mobile', 'country_code')}
                />
                {getNestedFieldError('mobile', 'country_code') && (
                  <FormText color="danger">{getNestedFieldError('mobile', 'country_code')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={8}>
              <FormGroup>
                <Label for="mobile.number" style={{ fontSize: '0.875rem' }}>Mobile Number *</Label>
                <Input
                  id="mobile.number"
                  name="mobile.number"
                  value={getNestedFieldValue('mobile', 'number')}
                  onChange={handleNestedFieldChange('mobile', 'number')}
                  onBlur={handleNestedFieldBlur('mobile', 'number')}
                  disabled={formik.isSubmitting}
                  invalid={!!getNestedFieldError('mobile', 'number')}
                  required
                />
                {getNestedFieldError('mobile', 'number') && (
                  <FormText color="danger">{getNestedFieldError('mobile', 'number')}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <FormGroup>
                <Label for="status" style={{ fontSize: '0.875rem' }}>Status *</Label>
                <Select
                  id="status"
                  name="status"
                  options={statusOptions}
                  value={getStatusValue()}
                  onChange={handleStatusChange}
                  onBlur={handleStatusBlur}
                  isClearable
                  isSearchable
                  styles={selectStyles}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={formik.isSubmitting}
                  placeholder="Select Status..."
                  required
                />
                {getFieldError('status') && (
                  <FormText color="danger">{getFieldError('status')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="reminder_hours" style={{ fontSize: '0.875rem' }}>Reminder Time in Hours (for Pending SOAs)</Label>
                <Input
                  id="reminder_hours"
                  name="reminder_hours"
                  type="number"
                  min="0"
                  step="1"
                  value={getFieldValue('reminder_hours', '')}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('reminder_hours')}
                  placeholder="e.g., 24"
                />
                <FormText color="muted">
                  Optional: Hours after which company owner receives reminder for pending SOAs.
                </FormText>
                {getFieldError('reminder_hours') && (
                  <FormText color="danger">{getFieldError('reminder_hours')}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <FormGroup check>
                <Input
                  id="two_factor_enabled"
                  name="two_factor_enabled"
                  type="checkbox"
                  checked={getFieldValue('two_factor_enabled', false)}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                />
                <Label for="two_factor_enabled" check style={{ fontSize: '0.875rem' }}>
                  Two-Factor Authentication
                </Label>
                {getFieldError('two_factor_enabled') && (
                  <FormText color="danger">{getFieldError('two_factor_enabled')}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button
            type="submit"
            color="primary"
            disabled={formik.isSubmitting || !user?.id}
          >
            {isEditMode ? 'Update' : 'Save'}
          </Button>
          <Button
            type="button"
            color="secondary"
            onClick={onClose}
            disabled={formik.isSubmitting}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Form>

      <style jsx>{`
        .react-select-container .react-select__control {
          border-radius: 0.375rem;
          transition: all 0.15s ease-in-out;
        }
        
        .react-select-container .react-select__control:hover {
          border-color: #007bff;
        }
        
        .react-select-container .react-select__menu {
          border-radius: 0.375rem;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </Modal>
  );
};

export { AddEditAuditorModal };